import React, { useEffect, useState, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import { userAPI } from '../services/api';
import { useI18n } from '../i18n/i18nContext';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { Package, SessionBundle } from '@shared/types';
import { useUser } from '../contexts/UserContext';
import ContentPageRenderer, { ContentPageData } from '../components/ContentPageRenderer';
import { axiosClient } from '../services/api';
import {
  pushToDataLayer,
  clearEcommerce,
  formatSessionBundleItem,
  formatPackageItem,
} from '../utils/dataLayer';
import MembershipTable from '../components/MembershipTable';
import SessionBundlesTable from '../components/SessionBundlesTable';
import { useSessionBundlesStore } from '../stores/sessionBundlesStore';
import SEO from '../components/SEO';
import { usePackageStore } from 'src/stores/packageStore';
import PackagePacksTable from 'src/components/PackagePacksTable';

const PricingPage: React.FC = () => {
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const { t, language } = useI18n();
  useDocumentTitle(t.pageTitles.pricing);
  const navigate = useLocalizedNavigate();
  const location = useLocation();
  const { user } = useUser();

  const {
    sessionBundles: storeSessionBundles,
    promotionDetails,
    loading: storeLoading,
    fetchSessionBundles,
  } = useSessionBundlesStore();

  const {
    packages,
    promotionDetails: packagePromotionDetails,
    isLoading: packageStoreLoading,
    loadPackages,
  } = usePackageStore();

  const [filteredSessionBundles, setFilteredSessionBundles] =
    useState<SessionBundle[]>(storeSessionBundles);
  const [contentPages, setContentPages] = useState<Record<string, ContentPageData | null>>({});
  const [contentLoading, setContentLoading] = useState(true);
  const [showSpousePricing, setShowSpousePricing] = useState(false);
  const [spouseFiltering, setSpouseFiltering] = useState(false);
  const hasContentFetched = useRef(false);
  const hasFetchedSessionBundles = useRef(false);
  const hasFetchedPackages = useRef(false);

  useEffect(() => {
    if (!hasFetchedSessionBundles.current) {
      hasFetchedSessionBundles.current = true;
      fetchSessionBundles();
    }
  }, [fetchSessionBundles]);

  useEffect(() => {
    if (!hasFetchedPackages.current) {
      hasFetchedPackages.current = true;
      loadPackages();
    }
  }, [loadPackages]);

  useEffect(() => {
    const applySpouseFiltering = async () => {
      const metaData = user?.metaData || {};
      const isLinked = metaData.spouse_status === 'linked';
      const isInviteeSpouse = isLinked && metaData.spouse_role === 'invitee';

      if (!isInviteeSpouse || !metaData.spouse_id || storeSessionBundles.length === 0) {
        setFilteredSessionBundles(storeSessionBundles);
        return;
      }

      setSpouseFiltering(true);
      try {
        const spousePriceResponse = await userAPI.getSpouseMaxPrice();
        const { maxSpousePrice } = spousePriceResponse.data;

        if (maxSpousePrice !== null && maxSpousePrice !== undefined) {
          const filtered = storeSessionBundles.filter((sessionBundle: SessionBundle) => {
            if (sessionBundle.isSpouseSessionBundle) {
              return (
                sessionBundle.originalPrice != null && sessionBundle.originalPrice <= maxSpousePrice
              );
            } else {
              return sessionBundle.price > maxSpousePrice;
            }
          });
          setFilteredSessionBundles(filtered);
        } else {
          setFilteredSessionBundles(storeSessionBundles);
        }
      } catch (error) {
        console.error('[Spouse Invitee] Error fetching spouse max price:', error);
        setFilteredSessionBundles(storeSessionBundles);
      } finally {
        setSpouseFiltering(false);
      }
    };

    applySpouseFiltering();
  }, [storeSessionBundles, user?.metaData]);

  useEffect(() => {
    if (hasContentFetched.current) return;
    hasContentFetched.current = true;

    const fetchContentPages = async () => {
      try {
        const slugs = [
          'pricing-intro',
          'pricing-packages-top',
          'pricing-packages-info',
          'pricing-subscriptions-top',
          'pricing-membership-info',
          'pricing-sessionBundles-info',
          'pricing-sessionBundles-top',
        ];
        const response = await axiosClient.get<Record<string, ContentPageData>>(
          `/api/content-pages/batch/${slugs.join(',')}`
        );
        setContentPages(response.data);
      } catch (err) {
        console.error('Failed to fetch content pages:', err);
      } finally {
        setContentLoading(false);
      }
    };

    fetchContentPages();
  }, []);

  useEffect(() => {
    if (location.hash) {
      const elementId = location.hash.substring(1);
      setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  const hasTrackedViewItemList = useRef(false);
  useEffect(() => {
    if (filteredSessionBundles.length > 0 && !storeLoading && !hasTrackedViewItemList.current) {
      hasTrackedViewItemList.current = true;
      clearEcommerce();
      pushToDataLayer('view_item_list', {
        content: { category: 'subscription' },
      });
    }
  }, [filteredSessionBundles, storeLoading]);

  const handlePurchase = (sessionBundleId: string) => {
    const sessionBundle = filteredSessionBundles.find((p) => p.id === sessionBundleId);
    const itemTypeSegment =
      sessionBundle?.type === 'subscription' ? 'subscription' : 'session-bundle';

    if (sessionBundle) {
      clearEcommerce();
      pushToDataLayer('add_to_cart', {
        content: {
          category: sessionBundle.type === 'subscription' ? 'subscription' : 'session_bundle',
        },
        ecommerce: {
          items: [
            formatSessionBundleItem(
              sessionBundle,
              sessionBundle.type === 'subscription' ? 'Subscription' : 'Session Bundle'
            ),
          ],
        },
      });
    }

    if (!isAuthenticated) {
      loginWithRedirect({
        appState: {
          returnTo: `/checkout/${itemTypeSegment}/${sessionBundleId}`,
        },
      });
    } else {
      navigate(`/checkout/${itemTypeSegment}/${sessionBundleId}`);
    }
  };

  const handlePackagePackPurchase = (pkg: Package, pack: NonNullable<Package['packs']>[number]) => {
    clearEcommerce();
    pushToDataLayer('add_to_cart', {
      content: { category: 'package_pack' },
      ecommerce: {
        items: [formatPackageItem(pkg, `${pack.package_count} pckg`)],
      },
    });

    if (!isAuthenticated) {
      loginWithRedirect({
        appState: {
          returnTo: `/checkout/package-pack/${pack.id}`,
        },
      });
    } else {
      navigate(`/checkout/package-pack/${pack.id}`);
    }
  };

  const loading = storeLoading || packageStoreLoading || spouseFiltering;

  if (loading && filteredSessionBundles.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-gray-500">{t.sessionBundles.loadingSessionBundles}</div>
      </div>
    );
  }

  const metaData = user?.metaData || {};
  const isLinked = metaData.spouse_status === 'linked';
  const isInviteeSpouse = isLinked && metaData.spouse_role === 'invitee';

  const displayedSessionBundles = (filteredSessionBundles || []).filter((sessionBundle) => {
    if (sessionBundle.price <= 0) {
      return false;
    }

    const now = new Date();
    const hasPromotionalPricing =
      sessionBundle.pricingRules &&
      sessionBundle.pricingRules.length > 0 &&
      sessionBundle.pricingRules.some((rule) => {
        const startValid = !rule.startDate || new Date(rule.startDate) <= now;
        const endValid = !rule.endDate || new Date(rule.endDate) >= now;
        return startValid && endValid && rule.priority >= 100;
      });

    if (hasPromotionalPricing) {
      return false;
    }

    if (isInviteeSpouse) {
      return true;
    } else {
      return sessionBundle.isSpouseSessionBundle === showSpousePricing;
    }
  });

  const subscriptions = displayedSessionBundles
    .filter((sessionBundle) => sessionBundle.type === 'subscription')
    .sort((a, b) => a.price - b.price);

  const bulkSessionBundles = displayedSessionBundles
    .filter((sessionBundle) => sessionBundle.type === 'bulk')
    .sort((a, b) => a.price - b.price);

  return (
    <div className="bg-gray-50 py-16">
      <SEO description={t.seo.pricingDescription} />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 text-center">
          <h1 className="mb-4 text-4xl font-bold text-gray-900">{t.pricing.title}</h1>
          <p className="text-lg text-gray-600">{t.pricing.subtitle}</p>
        </div>

        {!contentLoading && (
          <ContentPageRenderer
            slug="pricing-intro"
            className="mb-8 max-w-5xl"
            preloadedContent={contentPages['pricing-intro']}
          />
        )}

        {promotionDetails && promotionDetails.id !== 'regular' && (
          <div className="mx-auto mb-12 max-w-3xl rounded-lg border-2 border-green-300 bg-green-50 p-6">
            <div className="text-center">
              <div
                className="text-gray-700"
                dangerouslySetInnerHTML={{
                  __html:
                    promotionDetails.content[language as keyof typeof promotionDetails.content] ||
                    promotionDetails.content.en,
                }}
              />
            </div>
          </div>
        )}

        <div className="mx-auto max-w-5xl">
          <div id="packages">
            {packages.length > 0 && (
              <div>
                {!contentLoading && (
                  <ContentPageRenderer
                    slug="pricing-packages-top"
                    className="mb-6"
                    preloadedContent={contentPages['pricing-packages-top']}
                  />
                )}
                <PackagePacksTable
                  items={packages}
                  language={language}
                  onPurchase={handlePackagePackPurchase}
                  purchaseLabel={t.sessionBundles.purchase}
                  promotionTitle={
                    (packagePromotionDetails?.package_packs_title as any)?.[language] ||
                    (packagePromotionDetails?.title as any)?.[language] ||
                    packagePromotionDetails?.title?.en
                  }
                />

                {!contentLoading && (
                  <ContentPageRenderer
                    slug="pricing-packages-info"
                    preloadedContent={contentPages['pricing-packages-info']}
                  />
                )}
              </div>
            )}
          </div>

          <div id="sessionBundles">
            {bulkSessionBundles.length > 0 && (
              <div>
                {!contentLoading && (
                  <ContentPageRenderer
                    slug="pricing-sessionBundles-top"
                    className="mb-6 mt-8"
                    preloadedContent={contentPages['pricing-sessionBundles-top']}
                  />
                )}
                <SessionBundlesTable
                  items={bulkSessionBundles}
                  language={language}
                  onPurchase={(sessionBundle) => handlePurchase(sessionBundle.id)}
                  purchaseLabel={t.sessionBundles.purchase}
                  perSessionLabel={t.sessionBundles.perSession}
                  promotionTitle={
                    promotionDetails?.session_bundle_title?.[
                      language as keyof typeof promotionDetails.session_bundle_title
                    ] ||
                    promotionDetails?.title?.[language as keyof typeof promotionDetails.title] ||
                    promotionDetails?.title?.en
                  }
                />

                {!contentLoading && (
                  <ContentPageRenderer
                    slug="pricing-sessionBundles-info"
                    preloadedContent={contentPages['pricing-sessionBundles-info']}
                  />
                )}
              </div>
            )}
          </div>

          <div id="subscriptions">
            {subscriptions.length > 0 && (
              <div>
                {!contentLoading && (
                  <ContentPageRenderer
                    slug="pricing-subscriptions-top"
                    className="mb-6 mt-8"
                    preloadedContent={contentPages['pricing-subscriptions-top']}
                  />
                )}
                {/* Temporarily hidden per client request - Do not remove logic */}
                <div className="hidden">
                  {user?.metaData?.spouse_status !== 'linked' && (
                    <div className="flex items-center">
                      <span className="mr-4 text-gray-700">
                        {t.sessionBundles.showSpousePricing}
                      </span>
                      <label htmlFor="spouse-toggle" className="flex cursor-pointer items-center">
                        <div className="relative">
                          <input
                            id="spouse-toggle"
                            type="checkbox"
                            className="sr-only"
                            checked={showSpousePricing}
                            onChange={() => setShowSpousePricing(!showSpousePricing)}
                          />
                          <div
                            className={`block h-8 w-14 rounded-full transition-colors ${
                              showSpousePricing ? 'bg-blue-600' : 'bg-gray-300'
                            }`}
                          ></div>
                          <div
                            className={`dot absolute left-1 top-1 h-6 w-6 rounded-full bg-white transition-transform ${
                              showSpousePricing ? 'translate-x-6 transform' : ''
                            }`}
                          ></div>
                        </div>
                      </label>
                    </div>
                  )}
                </div>
                <MembershipTable
                  items={subscriptions}
                  language={language}
                  onSubscribe={(sessionBundle) => handlePurchase(sessionBundle.id)}
                  subscribeLabel={t.sessionBundles.subscribe}
                  perSessionLabel={t.sessionBundles.perSession}
                  promotionTitle={
                    promotionDetails?.title?.[language as keyof typeof promotionDetails.title] ||
                    promotionDetails?.title?.en
                  }
                />

                {!contentLoading && (
                  <ContentPageRenderer
                    slug="pricing-membership-info"
                    className="mt-8"
                    preloadedContent={contentPages['pricing-membership-info']}
                  />
                )}
              </div>
            )}
          </div>

          {showSpousePricing && (
            <div className="mt-8 text-center">
              <p className="text-sm text-gray-500">{t.sessionBundles.spouseDisclaimer}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
